/**
 * Assessment Components that a Student can perform activity within.
 *
 * An instance of a Component is unique in the Aphrodite client for a student.
 */
export type Component =
  | AssessmentComp
  | WorkComp
  | CharterComp
  | PeerComp
  | AnswerComp
  | FeedbackComp;

/**
 * Top-level Assessment component.
 */
export interface AssessmentComp {
  type: "assessment";
}

/** A Work component parameterised by a specific Work ID. */
export interface WorkComp {
  type: "work";
  workId: string;
}

/** Answer component for a specific answer. */
export interface AnswerComp {
  type: "answer";
  answerBlockId: string;
}

/** The group Charter component. */
export interface CharterComp {
  type: "charter";
}

/** Peer evaluation component. */
export interface PeerComp {
  type: "peer";
}

/** The final Feedback component. */
export interface FeedbackComp {
  type: "feedback";
}

/**
 * A ActivitySpan describes a span of activity that a Student is conducting
 * within their current Session.
 *
 * AcitivitySpans with the same `spanId` have occured close to each other within
 * an `IDLE_BUFFER` amount of time, and are the same kind of *continuous*
 * activity for the same `component`.
 *
 * A Client Tab puts the Student under exactly one Component at a time, so
 * exactly one Span is ever open. Moving between Components ends one Span and
 * opens the next, and returning to a Component opens a new Span rather than
 * resuming the one it had before: assessment, work, question 1, question 2,
 * work, question 3 is six moves and six Spans.
 *
 * Two Spans therefore never cover the same stretch of time, and summing their
 * durations gives the time the Student spent rather than a multiple of it.
 */
export interface ActivitySpan {
  spanId: string;
  component: Component;
  iat: number; // Issued At Time
}

const IDLE_BUFFER = 5 * 60 * 1000; // 5 minutes in milliseconds

// [private] The one open ActivitySpan, held for the Component the Student is
// currently under. Null until a Component declares itself.
let OPEN_SPAN: ActivitySpan | null = null;

// [private] Distinguishes Spans started within the same millisecond.
let SPAN_SEQUENCE = 0;

/**
 * Returns the open ActivitySpan, or null before any Component has declared
 * itself.
 *
 * The open Span is extended, so a Student who is still making requests is still
 * under their Component. A Span idle past `IDLE_BUFFER` is replaced by a new
 * Span for the same Component.
 */
export function currentActivitySpan(): ActivitySpan | null {
  if (OPEN_SPAN) {
    return startSpan(OPEN_SPAN.component);
  }
  return null;
}

/**
 * Declares that the Student is now under `component`, and returns the Span
 * covering their activity there.
 *
 * Moving to a different Component ends the open Span and opens a new one.
 * Staying put extends the open Span, keeping its `spanId`, until `IDLE_BUFFER`
 * passes with no activity.
 *
 * Call this from the page the Student is on, or from the navigation that takes
 * them there, never from an ancestor route. React runs a descendant's effects
 * before its ancestors', so an ancestor declaring on mount would overwrite what
 * the page beneath it had already declared and misattribute everything after.
 */
export function startSpan(component: Component): ActivitySpan {
  const now = Date.now();
  const open = OPEN_SPAN;

  if (
    open &&
    encodeComponent(open.component) === encodeComponent(component) &&
    !isExpiredSpan(open, now)
  ) {
    OPEN_SPAN = extendSpan(open, now);
  } else {
    OPEN_SPAN = newSpan(component, now);
  }

  return OPEN_SPAN;
}

function extendSpan(span: ActivitySpan, now: number) {
  return { ...span, iat: now };
}

function isExpiredSpan(span: ActivitySpan, now: number) {
  const expiry = span.iat + IDLE_BUFFER;
  return now > expiry;
}

function newSpan(type: Component, now: number): ActivitySpan {
  return {
    component: type,
    spanId: genSpanId(type),
    iat: now,
  };
}

/**
 * Encode the given ActivitySpan into a string suitable for use as a well-known
 * `x-cadmus-span` header for backend systems and service to understand.
 */
export function encodeSpanHeader(span: ActivitySpan): string {
  return span.spanId;
}

// Unique key ID for a SpanType
function encodeComponent(type: Component): string {
  switch (type.type) {
    case "assessment":
      return "assessment";
    case "work":
      return `work:${type.workId}`;
    case "charter":
      return "charter";
    case "peer":
      return "peer";
    case "answer":
      return `answer:${type.answerBlockId}`;
    case "feedback":
      return "feedback";
  }
}

/**
 * Time-based sortable span ID.
 *
 * The sequence distinguishes spans started within the same millisecond, which
 * two random digits did not: a Student moving off a Component and back within
 * the millisecond had a 1 in 100 chance of both Spans sharing an ID, and
 * consumers group by ID. It is zero padded so the ID stays a fixed width and
 * therefore sorts by time.
 */
function genSpanId(component: Component): string {
  SPAN_SEQUENCE = (SPAN_SEQUENCE + 1) % 1000;
  const seqId = SPAN_SEQUENCE.toString().padStart(3, "0");
  const timeId = new Date().getTime();
  const key = encodeComponent(component);
  return `span:${key}:${timeId}${seqId}`;
}
